<template>
    <div class="track-preview" :class="{
            disabled: props.disabled,
            invert: props.invert,
            light: props.light,
            skeleton: props.skeleton,
            'size-l': props.size === 'size-l',
            'size-m': props.size === 'size-m',
        }"
        @click="isMobile() ? toggle() : null"
    >
        <div>
            <div class="cover" @click.stop="" v-if="props.cover"
                :class="{
                    disabled: !props.item?.wave || locked
                }">

                <Img v-if="cover?.resized || defaultCover?.resized || props.skeleton"
                    :preview="props.skeleton ? null : cover?.resized || defaultCover?.resized"
                    :original="props.skeleton ? null : cover?.original || defaultCover?.original"
                    :skeleton="true"
                    :checktone="!!props.checkcolor"
                    @color="emit('color', $event)"
                    aspectRatio="1 / 1"
                />

                <AudioToggle v-if="props.item?.wave && props.playable && !locked"
                    :state="state"
                    @play="play"
                    @pause="pause"
                />

                <Icon v-if="locked" class="locked" icon="lock" />
                
                <Icon icon="exclamation"
                    v-if="props.item?.isExplicit"
                    class="explicit"
                />
            </div>
        </div>
        <div>
            <div
                :class="{
                    mask: props.item?.fromProject || props.item?.version,
                }"
            >
                <span class="title"
                    :class="{
                        full: props.item?.fromProject || props.item?.version
                    }"
                >
                    {{ props.item?.title || t('tracks.empty-name') }}
                </span>

                <span class="from" v-if="props.item?.fromProject || props.item?.version">
                    <template v-if="props.item?.fromProject">
                        {{ t('tracks.from') }} {{ props.item?.fromProject }}
                    </template>
                    <template v-if="props.item?.fromProject && props.item?.version">
                        /
                    </template>
                    <template v-if="props.item?.version">
                        {{ t(`tracks.versions.${props.item?.version}`) }}
                    </template>
                </span>

                <State :state="props.item?.state" v-if="props.showState && props.item?.state"/>
            </div>
            <div>
                <ContributorsPreview
                    :items="props.item?.contributors"
                    v-if="props.item?.contributors"
                    :invert="props.invert"
                    :class="props.size || ''"
                />

                <Button v-else-if="props.skeleton"
                    class="size-s tertiary gray link"
                    name="Artists"
                />
            </div>
            <div v-if="props.item?.contentId || props.item?.hasProRights || props.item?.isExplicit">
                <span v-if="props.item?.contentId" class="tag">{{ t('tracks.content-id') }}</span>
                <span v-if="props.item?.hasProRights" class="tag">{{ t('tracks.pro') }}</span>
                <span v-if="props.item?.isExplicit" class="tag">{{ t('tracks.is-explicit') }}</span>
            </div>
        </div>
        <div v-if="props.showKebab || props.showLike">
            <IconButton
                v-if="props.showKebab"
                @click.stop="openKebab"
                class="tertiary"
                :class="{
                    invert: props.invert,
                    'size-l': props.size === 'size-l',
                    'size-s': !props.size || props.size === 'size-m'
                }"
                icon="more-vertical"
            />

            <IconButton
                v-if="props.showLike"
                @click.stop=""
                class="tertiary size-l"
                :dunk="true"
                :class="{
                    invert: props.invert
                }"
                icon="unlike"
            />
        </div>
    </div>
</template>

<script setup>
    import { computed } from 'vue';
    import { useI18n } from 'vue-i18n';
    import Button from '🔗/components/ui/Button.vue';
    import IconButton from '🔗/components/ui/IconButton.vue';
    import Img from '🔗/components/ui/Img.vue';
    import ContributorsPreview from '🔗/components/ui/ContributorsPreview.vue';
    import AudioToggle from '🔗/components/ui/AudioToggle.vue';
    import api from '🔗/api';
    import stores from '@/stores';
    import State from '🔗/components/ui/State.vue';
    import { isMobile } from '🔗/utils/utils.js';

    const player = stores.player();
    const modals = stores.modals();

    const emit = defineEmits(['change', 'play', 'color']);

    const { t } = useI18n();

    const props = defineProps({
        item: { type: Object, default: () => ({}) },
        cover: { type: Boolean, default: true },
        showState: { type: Boolean, default: false },
        size: { type: [String, null], default: null },
        disabled: { type: Boolean, default: false },
        viewonly: { type: Boolean, default: false },
        light: { type: Boolean, default: false },
        showKebab: { type: Boolean, default: false },
        showLike: { type: Boolean, default: false },
        skeleton: { type: Boolean, default: false },
        playable: { type: Boolean, default: true },
        checkcolor: { type: Boolean, default: false },
        externalPlay: { type: Boolean, default: false },
        invert: { type: Boolean, default: false }
    });

    const cover = computed(() => {
        let url;
        if (props.item?.cover instanceof File) {
            url = URL.createObjectURL(props.item.cover);
        }

        return url ? {
            resized: url,
            cover: url,
            original: url
        } : props.item?.cover;
    });

    const locked = computed(() => {
        if (!props.item.releaseDate) {
            return false;
        }

        const releaseDate = new Date(props.item.releaseDate);
        const now = new Date();

        return releaseDate > now;
    });

    const defaultCover = computed(() => {
        return api.tracks().defaultCover;
    });

    const openKebab = () => {
        modals.open('track-kebab', { quietClose: true, data: { id: props.item?.id } })
    }

    const state = computed(() => {
        if (!player.panes.default || player.panes.default._item?.id !== props.item?.id) {
            return;
        }

        return player.panes.default._state;
    });


    const toggle = () => {
        if (props.viewonly) {
            return;
        }

        if (!props.item?.wave) {
            return;
        }

        if ([undefined, 'paused', 'stopped'].indexOf(state.value) !== -1) {
            play();
        } else if (state.value === 'playing') {
            pause();
        }
    }

    const play = async () => {
        if (props.viewonly) {
            return;
        }

        if (!state.value) {
            if (!props.externalPlay) {
                await player.track(props.item);

                player.play();
            }

            emit('play', props.item);

            return;
        }

        await player.play();
    }

    const pause = async () => {
        if (props.viewonly) {
            return;
        }

        await player.pause();
    }
</script>

<style lang="scss" scoped>
    .track-preview {
        display: flex;
        flex-direction: row;
        align-items: start;
        gap: 12px;
        width: 100%;
        overflow: hidden;
        & > div {
            &:first-child {
                display: flex;
                flex-direction: column;
                gap: 8px;

                .cover {
                    width: 68px;
                    height: 68px;
                    position: relative;

                    .skeleton-wrapper,
                    img {
                        width: 100%;
                        height: 100%;
                        border-radius: 10px;
                        object-fit: cover;
                        overflow: hidden;
                    }

                    .locked {
                        position: absolute;
                        fill: #ffffffcf;
                        z-index: 4;
                        top: 50%;
                        left: 50%;
                        width: 24px;
                        height: 24px;
                        transform: translateX(-50%) translateY(-50%);
                        stroke: none;
                        filter: drop-shadow(0px 0px 6px black);
                    }

                    .audio-toggle {
                        position: absolute;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                    }

                    .explicit {
                        position: absolute;
                        right: 0;
                        top: 0;
                        width: 12px;
                        height: 12px;
                        fill: var(--tertiary-600);
                    }

                    &.disabled {
                        filter: grayscale(1);
                        .skeleton-wrapper,
                        img {
                            cursor: default;
                        }
                    }
                }
            }
            &:nth-child(2) {
                display: flex;
                flex-direction: column;
                gap: 8px;
                flex: 1;
                min-width: 0;

                & > div {
                    display: flex;
                    flex-direction: row;
                    align-items: center;
                    gap: 4px;
                    min-width: 0;

                    &:first-child {
                        flex-wrap: nowrap;
                    }

                    .title {
                        font: var(--font-body-1);
                        color: var(--dark-500);
                        white-space: nowrap;
                        text-overflow: ellipsis;
                        overflow: hidden;
                        &.full {
                            flex-shrink: 0;
                            max-width: 84%;
                        }

                    }


                    .from {
                        font: var(--font-body-2);
                        white-space: nowrap;
                        color: var(--gray-80);
                        // flex-shrink: 0;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        // flex: 1;
                    }

                    .tag {
                        white-space: nowrap;
                        font: var(--font-label);
                        background: var(--gray-90);
                        padding: 4px 8px;
                        border-radius: 10px;
                        color: var(--white-500);
                        flex-shrink: 0;
                    }

                    .state {
                        margin-left: 4px;
                    }

                    &.mask {
                        mask-image: linear-gradient(to right, black 90%, transparent 100%);
                    }
                }
            }
            &:nth-child(3) {
                display: flex;
                flex-direction: column;
                flex-shrink: 0;
            }

            &:first-child:empty + div {
                margin-left: -8px;
            }
        }


        &.size-m {
            gap: 8px;
            align-items: center;
            & > div {
                &:first-child {
                    .cover {
                        width: 44px;
                        height: 44px;
                        pointer-events: none;
                        .audio-toggle {
                            display: none;
                        }
                    }
                }
                &:nth-child(2) {
                    gap: 4px
                }
            }
        }

        &.size-l {
            gap: 8px;
            align-items: center;
            & > div {
                &:first-child {
                    .cover {
                        width: 44px;
                        height: 44px;
                        pointer-events: none;
                        .audio-toggle {
                            display: none;
                        }
                    }
                }
                &:nth-child(2) {
                    gap: 4px;

                    & > div {
                        .title {
                            font: var(--font-h6);
                            color: var(--dark-500);
                        }

                        .from {
                            font: var(--font-body-1);
                        }
                    }
                }
            }
        }

        &.invert {
            & > div {
                &:nth-child(2) {
                    & > div {
                        .title {
                            color: var(--white-500);
                        }
                    }
                }
            }
        }

        &.disabled {
            opacity: .4;
            filter: grayscale(1);
            cursor: default;
        }

        &.light {
            align-items: center;
            & > div {
                &:first-child {
                    .cover {
                        width: 48px;
                        height: 48px;
                    }
                }
            }

            & > div:nth-child(2) {
                gap: 4px;
                & > div:nth-child(3) {
                    display: none;
                }
            }
        }

        &.skeleton {
            & > div:nth-child(2) {
                & > div {
                    position: relative !important;
                    color: transparent !important;
                    @include skeleton;

                    &:nth-child(1) {
                        max-width: 120px;
                        .title {
                            opacity: 0;
                        }
                    }
                    &:nth-child(2) {
                        max-width: 60px;
                        button {
                            opacity: 0;
                        }
                    }
                }

            }
        }
    }

    .direction-rtl {
        .track-preview {
            & > div {
                &:nth-child(2) {
                    & > div {
                        &.mask {
                            mask-image: linear-gradient(to left, black 90%, transparent 100%);
                        }
                    }
                }
            }
        }
    }
</style>